import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useState } from 'react';
import { ScheduledBadge } from '@/components/cashier-pos/ScheduledServiceField';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Undo2, Receipt, ChevronLeft, Edit, Trash2, Printer, MessageCircle, Mail, ShieldCheck, ShieldAlert, ShieldQuestion, RefreshCw, Download, Search } from 'lucide-react';

/** ZATCA compliance status pill. Returns null when the tenant isn't
 *  onboarded (order has no `zatca` field). */
function ZatcaStatusBadge({ zatca }) {
  if (!zatca || !zatca.status) return null;
  const s = zatca.status;
  const map = {
    REPORTED: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck, label: 'ZATCA ✓ Reported' },
    CLEARED: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck, label: 'ZATCA ✓ Cleared' },
    PENDING: { cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: ShieldQuestion, label: 'ZATCA · Pending' },
    FAILED: { cls: 'bg-red-100 text-red-700 border-red-200', icon: ShieldAlert, label: 'ZATCA · Failed' },
    HELD: { cls: 'bg-violet-100 text-violet-700 border-violet-200', icon: ShieldQuestion, label: 'ZATCA · Held (historical ref)' },
  };
  const cfg = map[s] || map.PENDING;
  const Icon = cfg.icon;
  return (
    <Badge className={`text-[10px] gap-1 border ${cfg.cls}`} data-testid={`zatca-status-${s.toLowerCase()}`}>
      <Icon size={11} /> {cfg.label}
    </Badge>
  );
}

/**
 * Today's order history dialog. Has two modes:
 *  - list mode: rows of today's orders
 *  - detail mode (when `viewingOrder` set): full breakdown + actions
 *    (Edit, Void, Receipt)
 * All mutations (edit/void/receipt) are handled via callbacks from parent.
 */
export default function OrderHistoryDialog({
  open,
  onOpenChange,
  orders,
  loadingOrders,
  viewingOrder,
  setViewingOrder,
  onEditOrder,
  onDeleteOrder,
  onRefundOrder,
  canRefund = false,
  onPrintReceipt,
  onResendWhatsApp,
  onResendEmail,
  onRetryZatca,
  retryingZatca,
  onDownloadInvoice,
  downloadingInvoice,
  billOptions = { reprint_resend: true },
  branchTax,
  currencySymbol = 'SAR',
}) {
  // iter917 — searchable Order ID / customer name / phone (incl. tailor metadata).
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const visibleOrders = !q ? orders : orders.filter((o) =>
    String(o.order_number || '').toLowerCase().includes(q) ||
    String(o.customer_name || '').toLowerCase().includes(q) ||
    String(o.metadata?.customer_name || '').toLowerCase().includes(q) ||
    String(o.metadata?.customer_phone || '').toLowerCase().includes(q)
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2">
            <Receipt size={20} /> Today's Orders
          </DialogTitle>
        </DialogHeader>

        {viewingOrder ? (
          <div className="space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setViewingOrder(null)} className="mb-2">
              <ChevronLeft size={16} className="mr-1" /> Back to list
            </Button>
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-lg">Order #{viewingOrder.order_number}</h3>
                <Badge className={viewingOrder.status === 'completed' ? 'bg-success/20 text-success' : viewingOrder.status === 'preparing' ? 'bg-warning/20 text-warning' : 'bg-muted'}>
                  {viewingOrder.status}
                </Badge>
              </div>
              <div className="text-sm text-muted-foreground grid grid-cols-2 gap-2">
                <span>Type: {viewingOrder.order_type?.replace('_', ' ')}</span>
                <span>Payment: {viewingOrder.payment_method}</span>
                {viewingOrder.table_number && <span>Table: {viewingOrder.table_number}</span>}
                {viewingOrder.customer_name && <span>Customer: {viewingOrder.customer_name}</span>}
                <span>Cashier: {viewingOrder.cashier_name}</span>
                <span>Time: {viewingOrder.created_at ? new Date(viewingOrder.created_at).toLocaleTimeString() : '-'}</span>
              </div>

              <div className="border rounded-lg overflow-hidden mt-3">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left">Item</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewingOrder.items || []).map((item, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{item.name}</td>
                          <td className="px-3 py-2 text-center">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">{currencySymbol} {item.unit_price?.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">{currencySymbol} {item.subtotal?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-sm space-y-1 pt-2 border-t">
                <div className="flex justify-between"><span>Subtotal</span><span>{currencySymbol} {viewingOrder.subtotal?.toFixed(2)}</span></div>
                {viewingOrder.discount > 0 && (
                  <div className="flex justify-between text-error">
                    <span>Discount</span>
                    <span>-{currencySymbol} {viewingOrder.discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>{branchTax.tax_name} ({branchTax.tax_rate}%)</span>
                  <span>{currencySymbol} {viewingOrder.tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-base">
                  <span>Total</span>
                  <span>{currencySymbol} {viewingOrder.total?.toFixed(2)}</span>
                </div>
              </div>
              {viewingOrder.notes && (
                <div className="text-sm text-muted-foreground mt-2">Notes: {viewingOrder.notes}</div>
              )}
            </div>

            {/* ZATCA compliance status + retry (only when tenant is onboarded) */}
            {viewingOrder.zatca?.status && (
              <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30" data-testid="zatca-detail-panel">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">E-Invoice (ZATCA)</span>
                    <ZatcaStatusBadge zatca={viewingOrder.zatca} />
                  </div>
                  {viewingOrder.zatca.status === 'FAILED' && viewingOrder.zatca.detail && (
                    <p className="text-xs text-red-600 max-w-sm truncate" title={viewingOrder.zatca.detail}>
                      {viewingOrder.zatca.detail}
                    </p>
                  )}
                </div>
                {['FAILED', 'PENDING'].includes(viewingOrder.zatca.status) && onRetryZatca && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    disabled={retryingZatca === viewingOrder.id}
                    onClick={() => onRetryZatca(viewingOrder)}
                    data-testid="zatca-retry-btn"
                  >
                    <RefreshCw size={14} className={`mr-1 ${retryingZatca === viewingOrder.id ? 'animate-spin' : ''}`} />
                    {retryingZatca === viewingOrder.id ? 'Retrying…' : 'Retry report'}
                  </Button>
                )}
                {['CLEARED', 'REPORTED'].includes(viewingOrder.zatca.status) && onDownloadInvoice && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    disabled={downloadingInvoice === viewingOrder.id}
                    onClick={() => onDownloadInvoice(viewingOrder)}
                    data-testid="zatca-download-invoice-btn"
                  >
                    <Download size={14} className={`mr-1 ${downloadingInvoice === viewingOrder.id ? 'animate-pulse' : ''}`} />
                    {downloadingInvoice === viewingOrder.id ? 'Preparing…' : 'Tax invoice PDF'}
                  </Button>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                onClick={() => { onEditOrder(viewingOrder); setViewingOrder(null); }}
                data-testid="edit-order-btn"
              >
                <Edit size={16} className="mr-1" /> Edit Order
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => { onDeleteOrder(viewingOrder.id); setViewingOrder(null); }}
                data-testid="void-order-btn"
              >
                <Trash2 size={16} className="mr-1" /> Void Order
              </Button>
              {canRefund && onRefundOrder && !viewingOrder.is_refund && !viewingOrder.refund_of
                && !['open', 'voided', 'refunded'].includes(viewingOrder.status) && (
                <Button variant="outline" className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => onRefundOrder(viewingOrder)} data-testid="refund-order-btn">
                  <Undo2 size={16} className="mr-1" /> Refund
                </Button>
              )}
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onPrintReceipt(viewingOrder.id)}
                data-testid="receipt-order-btn"
              >
                <Printer size={16} className="mr-1" /> Reprint
              </Button>
            </div>

            {billOptions?.reprint_resend !== false && (
              <div className="flex gap-2" data-testid="resend-actions">
                <Button
                  variant="outline"
                  className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => onResendWhatsApp && onResendWhatsApp(viewingOrder)}
                  data-testid="resend-whatsapp-btn"
                >
                  <MessageCircle size={16} className="mr-1" /> Resend WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => onResendEmail && onResendEmail(viewingOrder)}
                  data-testid="resend-email-btn"
                >
                  <Mail size={16} className="mr-1" /> Resend Email
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative" data-testid="order-history-search-wrap">
              <Search size={14} className="absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search Order ID, customer, phone… / رقم الطلب، العميل، الجوال"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9 rounded-xl"
                data-testid="order-history-search-input"
              />
            </div>
            {loadingOrders ? (
              <div className="text-center py-8 text-muted-foreground">Loading orders...</div>
            ) : visibleOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{q ? 'No matching orders' : 'No orders today'}</div>
            ) : (
              visibleOrders.map((order) => (
                <div
                  key={order.id}
                  className="border rounded-lg p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setViewingOrder(order)}
                  data-testid={`order-row-${order.order_number}`}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-100 text-orange-700 font-bold rounded-lg px-3 py-1 text-sm">
                        #{order.order_number}
                      </div>
                      <ScheduledBadge iso={order.scheduled_for} testId={`order-scheduled-${order.order_number}`} />
                      <div>
                        <p className="text-sm font-medium">
                          {(order.items || []).length} item(s) - {order.order_type?.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {order.created_at ? new Date(order.created_at).toLocaleTimeString() : ''}
                          {order.cashier_name ? ` · ${order.cashier_name}` : ''}
                          {order.customer_name ? ` · ${order.customer_name}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm">{currencySymbol} {order.total?.toFixed(2)}</p>
                      <div className="flex items-center gap-1 justify-end flex-wrap">
                        <Badge className={`text-[10px] ${order.status === 'completed' ? 'bg-success/20 text-success' : order.status === 'preparing' ? 'bg-warning/20 text-warning' : 'bg-muted'}`}>
                          {order.payment_method} · {order.status}
                        </Badge>
                        <ZatcaStatusBadge zatca={order.zatca} />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
